import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { TIERS, formatRp } from "@/lib/tiers";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Eye, Check, X, ZoomIn, ZoomOut, RotateCw, Maximize2 } from "lucide-react";

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

export default function AdminPayments() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [view, setView] = useState(null);     // currently-previewed payment (modal)
  const [note, setNote] = useState("");
  const [deciding, setDeciding] = useState(false);

  // Modal zoom/rotate state
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/payments", { params: { status: tab } });
      setItems(data.payments || []);
    } catch (e) {
      console.error("Failed to load payments:", e);
      toast.error("Failed to load payments");
    }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const openModal = (p) => {
    setView(p);
    setNote(p.admin_note || "");
    setZoom(1);
    setRotation(0);
  };
  const closeModal = () => { setView(null); setNote(""); setZoom(1); setRotation(0); };

  const decide = async (id, action) => {
    setDeciding(true);
    try {
      await api.post(`/admin/payments/${id}/decide`, { action, admin_note: note });
      toast.success(`Payment ${action}d`);
      closeModal();
      load();
    } catch (e) {
      console.error("Decide payment failed:", e);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setDeciding(false); }
  };

  const proofUrlResolved = useMemo(() => fileUrl(view?.proof_url || ""), [view]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold text-white">Payment Verification</h2>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-zinc-950 border border-white/10">
            <TabsTrigger value="pending" data-testid="payments-tab-pending">Pending</TabsTrigger>
            <TabsTrigger value="approved" data-testid="payments-tab-approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="payments-tab-rejected">Rejected</TabsTrigger>
            <TabsTrigger value="all" data-testid="payments-tab-all">All</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="glass rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-zinc-400 text-xs uppercase tracking-wider bg-black/40">
              <tr>
                <th className="text-left p-3">When</th>
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">Tier</th>
                <th className="text-left p-3">Method</th>
                <th className="text-left p-3">Amount</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-white/5" data-testid={`admin-payment-row-${p.id}`}>
                  <td className="p-3 text-zinc-400 text-xs">{new Date(p.created_at).toLocaleString()}</td>
                  <td className="p-3 text-white">{p.user_name}<div className="text-xs text-zinc-500">{p.user_email}</div></td>
                  <td className="p-3"><Badge className={TIERS[p.tier]?.chip}>{TIERS[p.tier]?.label}</Badge></td>
                  <td className="p-3 uppercase text-zinc-300">{p.method}</td>
                  <td className="p-3 text-white font-semibold">{formatRp(p.amount)}</td>
                  <td className="p-3">
                    <Badge className={p.status === "approved" ? "bg-emerald-900/40 text-emerald-300 border border-emerald-500/30" : p.status === "rejected" ? "bg-rose-900/40 text-rose-300 border border-rose-500/30" : "bg-amber-900/40 text-amber-300 border border-amber-500/30"}>{p.status}</Badge>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openModal(p)} data-testid={`view-payment-${p.id}`}>
                        <Eye className="w-3 h-3 mr-1" />Quick view
                      </Button>
                      <Link to={`/admin/payments/${p.id}/proof`} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="ghost" className="text-rose-300 hover:text-rose-200" title="Open full proof viewer in new tab" data-testid={`open-proof-${p.id}`}>
                          <Maximize2 className="w-3 h-3" />
                        </Button>
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={7} className="text-center p-8 text-zinc-500">No payments.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && closeModal()}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3">
              <span>Payment Proof — Quick Review</span>
              {view && (
                <Link to={`/admin/payments/${view.id}/proof`} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" className="text-rose-300 border-rose-500/40" data-testid="open-full-viewer">
                    <Maximize2 className="w-3 h-3 mr-1" /> Open full viewer
                  </Button>
                </Link>
              )}
            </DialogTitle>
          </DialogHeader>
          {view && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Field label="User" value={`${view.user_name} (${view.user_email})`} />
                <Field label="Tier" value={TIERS[view.tier]?.label} />
                <Field label="Method" value={view.method.toUpperCase()} />
                <Field label="Amount" value={formatRp(view.amount)} />
                <Field label="Sender Name" value={view.sender_name} />
                <Field label="Sender Account" value={view.sender_account || "—"} />
                <Field label="Note" value={view.note || "—"} />
                <Field label="Submitted" value={new Date(view.created_at).toLocaleString()} />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-zinc-500 uppercase">Proof</div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)))} data-testid="modal-zoom-out">
                      <ZoomOut className="w-3 h-3" />
                    </Button>
                    <span className="text-xs text-zinc-300 px-1 font-mono w-12 text-center">{Math.round(zoom * 100)}%</span>
                    <Button size="sm" variant="ghost" onClick={() => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)))} data-testid="modal-zoom-in">
                      <ZoomIn className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setRotation((r) => (r + 90) % 360)} data-testid="modal-rotate">
                      <RotateCw className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="bg-[radial-gradient(circle_at_center,#1c1c1f_0%,#0a0a0c_100%)] rounded border border-white/10 max-h-[60vh] overflow-auto grid place-items-center p-4">
                  <img
                    src={proofUrlResolved}
                    alt="proof"
                    style={{ transform: `scale(${zoom}) rotate(${rotation}deg)`, transition: "transform 120ms ease-out" }}
                    className="block max-w-full"
                    draggable={false}
                    data-testid="modal-proof-image"
                  />
                </div>
              </div>

              <div>
                <div className="text-xs text-zinc-500 uppercase mb-2">Admin Note</div>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="bg-black/40 border-white/10" data-testid="admin-payment-note" />
              </div>

              {view.status === "pending" && (
                <div className="flex gap-3">
                  <Button onClick={() => decide(view.id, "approve")} disabled={deciding} className="flex-1 bg-emerald-600 hover:bg-emerald-500" data-testid="approve-payment-btn">
                    <Check className="w-4 h-4 mr-2" />Approve
                  </Button>
                  <Button onClick={() => decide(view.id, "reject")} disabled={deciding} className="flex-1 bg-rose-600 hover:bg-rose-500" data-testid="reject-payment-btn">
                    <X className="w-4 h-4 mr-2" />Reject
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className="text-white mt-0.5">{value}</div>
    </div>
  );
}
